import { useEffect, useRef, useState } from 'react'
import { useStore } from '../store/useStore'
import { useIsMobileOrTablet } from '../hooks/useMediaQuery'
import { Maximize2, Minimize2, RotateCcw, ExternalLink } from 'lucide-react'

export default function Preview() {
  const iframeRef = useRef(null)
  const abortControllerRef = useRef(null)
  const debounceTimerRef = useRef(null)
  const runIdRef = useRef(0)
  const lastHtmlRef = useRef(null) // Store generated HTML for popout
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isPoppedOut, setIsPoppedOut] = useState(false)
  const { files, p5Version, setLastCanvasImage } = useStore()
  
  // Detect mobile/tablet (reactive to window resize)
  const isMobileOrTablet = useIsMobileOrTablet()

  // Effect A: Event listeners (runs once, not on every file change)
  useEffect(() => {
    const handleRun = () => runSketch()
    const handleStop = () => stopSketch()
    
    window.addEventListener('runSketch', handleRun)
    window.addEventListener('stopSketch', handleStop)
    
    return () => {
      window.removeEventListener('runSketch', handleRun)
      window.removeEventListener('stopSketch', handleStop)
    }
  }, [])

  // Effect B: Debounced auto-run on file changes (prevents run on every keystroke)
  useEffect(() => {
    // Clear any pending debounced run
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    
    // Schedule new run after 350ms of no changes
    debounceTimerRef.current = setTimeout(() => {
      runSketch()
    }, 350)
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [files])

  const runSketch = async () => {
    if (!iframeRef.current) return

    // Notify listeners that sketch is starting (for MobileLayout sync)
    window.dispatchEvent(new CustomEvent('sketchStarted'))

    // Clear any pending debounce timer (prevents double-run after manual Run)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    // Cancel any in-flight request from previous run
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    
    // Track this run's ID to ignore stale responses
    const thisRunId = ++runIdRef.current

    // IMPORTANT: Read files fresh from store to avoid stale closure
    // (event listeners capture old runSketch, so we must read current state)
    const currentFiles = useStore.getState().files

    // Get file contents
    const jsFiles = currentFiles.filter(f => f.name.endsWith('.js'))
    const htmlFile = currentFiles.find(f => f.name.endsWith('.html'))
    const cssFile = currentFiles.find(f => f.name.endsWith('.css'))

    // Build HTML with injected code
    let html = htmlFile?.content || generateDefaultHTML()
    
    // Ensure HTML has proper structure
    if (!html.includes('</head>')) {
      html = generateDefaultHTML()
    }
    if (!html.includes('</body>')) {
      html = html.replace('</html>', '</body>\n</html>')
    }
    
    // Build the full HTML with proper error handling
    let fullHTML = html
    
    // ONLY replace local file references with actual content
    // Users control their own CDN scripts - we don't touch them
    
    // Replace <link href="style.css"> with inline styles
    const cssFiles = currentFiles.filter(f => f.name.endsWith('.css'))
    cssFiles.forEach(cssFile => {
      const linkRegex = new RegExp(`<link[^>]*href=["']${cssFile.name}["'][^>]*>`, 'gi')
      // Replace with inline style tag
      fullHTML = fullHTML.replace(linkRegex, `<style>/* ${cssFile.name} */\n${cssFile.content}\n</style>`)
    })
    
    // Replace <script src="sketch.js"> etc with inline scripts
    jsFiles.forEach(jsFile => {
      const scriptRegex = new RegExp(`<script[^>]*src=["']${jsFile.name}["'][^>]*>\\s*</script>`, 'gi')
      const beforeReplace = fullHTML
      // Replace with inline script tag - NO try/catch wrapper so p5 can see global functions
      // Add sourceURL directive so browser console shows correct filename in errors
      fullHTML = fullHTML.replace(scriptRegex, `<script>\n${jsFile.content}\n//# sourceURL=${jsFile.name}\n</script>`)
      
      // Debug: Check if replacement happened
      if (beforeReplace === fullHTML) {
        console.warn(`[Preview] Script tag for "${jsFile.name}" not found in HTML. Looking for: <script src="${jsFile.name}"></script>`)
      }
    })
    
    // Handle data files (.txt, .json, .csv, etc.) for loadStrings, loadJSON, loadTable
    // Uses blob URLs like the p5.js web editor does
    const dataFiles = currentFiles.filter(f => 
      !f.name.endsWith('.js') && 
      !f.name.endsWith('.html') && 
      !f.name.endsWith('.css')
    )
    
    if (dataFiles.length > 0) {
      // Create URLs for each data file and replace references in JS code
      const fileUrls = {}
      dataFiles.forEach(f => {
        // If content is already a data URL (base64 image/audio), use it directly
        if (f.content && (f.content.startsWith('data:image/') || f.content.startsWith('data:audio/'))) {
          fileUrls[f.name] = f.content
        } else {
          // For text-based files, create blob URL
          let mimeType = 'text/plain'
          if (f.name.endsWith('.json')) mimeType = 'application/json'
          else if (f.name.endsWith('.csv')) mimeType = 'text/csv'
          else if (f.name.endsWith('.xml')) mimeType = 'application/xml'
          else if (f.name.endsWith('.svg')) mimeType = 'image/svg+xml'
          
          const blob = new Blob([f.content], { type: mimeType })
          fileUrls[f.name] = URL.createObjectURL(blob)
        }
      })
      
      // Replace file references in inline scripts with URLs
      // Match strings like 'poem.txt' or "data.json" or 'image.png'
      Object.entries(fileUrls).forEach(([fileName, fileUrl]) => {
        // Escape special regex characters in filename
        const escapedName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Escape $ in fileUrl to prevent replacement pattern interpretation
        // In JS replace(), $$ becomes literal $ - base64 URLs often contain $
        const safeUrl = fileUrl.replace(/\$/g, '$$$$')
        // Replace both single and double quoted references
        const regex = new RegExp(`(['"])${escapedName}\\1`, 'g')
        fullHTML = fullHTML.replace(regex, `$1${safeUrl}$1`)
      })
    }
    
    // If HTML has NO p5.js script at all, inject our default version
    const hasAnyP5Script = /<script[^>]*src=["'][^"']*p5[^"']*["'][^>]*>/i.test(fullHTML)
    if (!hasAnyP5Script) {
      // User didn't specify - add default p5.js
      fullHTML = fullHTML.replace(
        '</head>',
        `\n      <script src="https://cdn.jsdelivr.net/npm/p5@${p5Version}/lib/p5.js"></script>\n      </head>`
      )
    }
    
    // Inject console interception at START of head (before any CDN scripts load)
    // This ensures we catch errors from external libraries like p5play, p5.sound
    fullHTML = fullHTML.replace(
      /<head[^>]*>/i,
      `$&
      <script>
        // Enhanced console and error interception with full stack traces
        // MUST be first script in head to catch all errors including CDN libs
        
        // DEBUG: Confirm this script is running
        console.log('[p5-ai-studio] Installing error handlers...');
        
        // Security: Use specific origin instead of '*' for postMessage
        const parentOrigin = window.location.origin;
        
// Helper to safely post messages only when in iframe (not popup window)
        function safePostToParent(data) {
          if (window.parent !== window) {
            window.parent.postMessage(data, parentOrigin);
          }
        }
        
        const originalLog = console.log;
        const originalError = console.error;
        const originalWarn = console.warn;
        const originalInfo = console.info;
        
        // Helper to serialize error objects
        function serializeError(error) {
          if (error instanceof Error) {
            return {
              message: error.message,
              stack: error.stack,
              name: error.name,
              fileName: error.fileName,
              lineNumber: error.lineNumber,
              columnNumber: error.columnNumber
            };
          }
          return error;
        }
        
        // Helper to format arguments - handles Error, DOMException, and error-like objects
        function formatArgs(args) {
          return args.map(arg => {
            // Handle Error objects (non-enumerable properties won't JSON.stringify)
            if (arg instanceof Error) {
              return (arg.name || 'Error') + ': ' + arg.message;
            }
            // Handle DOMException and other error-like objects with message property
            // These also have non-enumerable properties that JSON.stringify misses
            if (arg && typeof arg === 'object' && 'message' in arg) {
              return (arg.name || 'Error') + ': ' + arg.message;
            }
            try {
              return typeof arg === 'object' ? JSON.stringify(arg) : String(arg);
            } catch {
              return String(arg);
            }
          });
        }
        
        console.log = function(...args) {
          originalLog.apply(console, args);
          safePostToParent({
            type: 'console',
            level: 'log',
            message: formatArgs(args).join(' '),
            timestamp: new Date().toISOString()
          });
        };
        
        console.info = function(...args) {
          originalInfo.apply(console, args);
          safePostToParent({
            type: 'console',
            level: 'info',
            message: formatArgs(args).join(' '),
            timestamp: new Date().toISOString()
          });
        };
        
        console.error = function(...args) {
          originalError.apply(console, args);
          const formatted = formatArgs(args);
          const error = args.find(arg => arg instanceof Error);
          
          safePostToParent({
            type: 'console',
            level: 'error',
            message: formatted.join(' '),
            error: error ? serializeError(error) : null,
            stack: error?.stack || null,
            timestamp: new Date().toISOString()
          });
        };
        
        console.warn = function(...args) {
          originalWarn.apply(console, args);
          safePostToParent({
            type: 'console',
            level: 'warn',
            message: formatArgs(args).join(' '),
            timestamp: new Date().toISOString()
          });
        };
        
        // Enhanced global error handler with full stack trace
        window.onerror = function(msg, source, line, col, error) {
          const errorObj = {
            type: 'console',
            level: 'error',
            message: String(msg || ''),
            fileName: source,
            lineNumber: line,
            columnNumber: col,
            timestamp: new Date().toISOString()
          };
          
          if (error) {
            errorObj.error = serializeError(error);
            errorObj.stack = error.stack;
            errorObj.message = error.message || String(msg);
          }
          
          // Log to browser console too for debugging
          originalError('Captured error:', error || msg);
          safePostToParent(errorObj);
          return false;
        };
        
        // Enhanced unhandled promise rejection handler
        // Use addEventListener instead of onunhandledrejection property
        // This cannot be overridden by p5.js or other code (more robust)
        window.addEventListener('unhandledrejection', function(event) {
          const reason = event.reason;
          
          // Build error info robustly - handle Error, DOMException, strings, objects
          // Key insight: Error/DOMException have non-enumerable properties that JSON.stringify misses
          let errorMessage = 'Unknown promise rejection';
          let errorName = 'PromiseRejection';
          let errorStack = null;
          
          if (reason instanceof Error) {
            // Standard Error object
            errorMessage = reason.message || String(reason);
            errorName = reason.name || 'Error';
            errorStack = reason.stack || null;
          } else if (reason && typeof reason === 'object' && 'message' in reason) {
            // DOMException and error-like objects (have message but aren't instanceof Error)
            errorMessage = reason.message || String(reason);
            errorName = reason.name || 'Error';
            errorStack = reason.stack || null;
          } else if (typeof reason === 'string') {
            errorMessage = reason;
          } else {
            // Try to stringify, fallback to String()
            try {
              errorMessage = JSON.stringify(reason);
            } catch {
              errorMessage = String(reason);
            }
          }
          
          // Post DIRECTLY to parent - don't rely on console.error override
          // This ensures the error is captured even if console.error has issues
          try {
            safePostToParent({
              type: 'console',
              level: 'error',
              message: 'Unhandled promise rejection: ' + errorName + ': ' + errorMessage,
              error: { name: errorName, message: errorMessage, stack: errorStack },
              stack: errorStack,
              timestamp: new Date().toISOString()
            });
          } catch (postError) {
            // Fallback: at least log to browser console
            originalError('[p5-ai-studio] Failed to post rejection:', postError);
          }
          
          // Also log to browser console for debugging
          originalError('[p5-ai-studio iframe] Unhandled rejection:', errorName + ':', errorMessage);
          
          // NOTE: Do NOT call event.preventDefault() here!
          // That would suppress the browser's native "Uncaught (in promise)" console message
          // We want BOTH: our Advanced Error Console capture AND the browser's native display
        });
        
        // Long Task API - detect blocking tasks (like p5play/p5sound init)
        // These cause Chrome [Violation] warnings which we can't capture directly
        if (typeof PerformanceObserver !== 'undefined') {
          try {
            const longTaskObserver = new PerformanceObserver((list) => {
              for (const entry of list.getEntries()) {
                if (entry.duration > 100) {
                  safePostToParent({
                    type: 'console',
                    level: 'warn',
                    message: '[Performance] Long task detected: ' + entry.duration.toFixed(0) + 'ms (source: ' + entry.name + ')',
                    timestamp: new Date().toISOString()
                  });
                }
              }
            });
            longTaskObserver.observe({type: 'longtask', buffered: true});
          } catch (e) { /* Long Task API not supported */ }
        }
        
        // Blank canvas detection - check after 2.5 seconds if canvas is BLACK (truly blank)
        // Note: A solid background color is VALID, we only warn for black/transparent
        setTimeout(function() {
          try {
            const canvas = document.querySelector('canvas');
            if (canvas && canvas.width > 0 && canvas.height > 0) {
              const ctx = canvas.getContext('2d');
              if (ctx) {
                // Sample a small portion to check if blank
                const sampleW = Math.min(canvas.width, 50);
                const sampleH = Math.min(canvas.height, 50);
                const data = ctx.getImageData(0, 0, sampleW, sampleH).data;
                
                // Check if canvas is BLACK (0,0,0) or nearly black - this indicates no draw happened
                // A solid colored background() is intentional and valid
                let isBlack = true;
                let isUniform = true;
                const firstR = data[0], firstG = data[1], firstB = data[2];
                
                for (let i = 0; i < data.length; i += 16) {
                  // Check if NOT black (allow small variance for antialiasing)
                  if (data[i] > 10 || data[i+1] > 10 || data[i+2] > 10) {
                    isBlack = false;
                  }
                  // Check if pixels vary (meaning something is drawn)
                  if (Math.abs(data[i] - firstR) > 5 || 
                      Math.abs(data[i+1] - firstG) > 5 || 
                      Math.abs(data[i+2] - firstB) > 5) {
                    isUniform = false;
                  }
                }
                
                // Only warn if canvas is BLACK and uniform (nothing rendered at all)
                if (isBlack && isUniform) {
                  safePostToParent({
                    type: 'console',
                    level: 'warn',
                    message: 'Warning: Canvas appears black/empty after 2.5 seconds.',
                    timestamp: new Date().toISOString()
                  });
                }
              }
            }
          } catch (e) {
            // Canvas might be cross-origin tainted, ignore
          }
        }, 2500);
        
        // DEBUG: Confirm all handlers installed
        originalLog('[p5-ai-studio] Error handlers installed successfully');
      </script>`
    )
    
    // Note: We already injected all JS files inline where they were referenced
    // No need to inject again at the end

    // Upload HTML to preview cache and load from HTTPS endpoint
    // This enables webcam/microphone access (requires secure origin)
    const previewId = crypto.randomUUID()
    
    // Store HTML for popout (before sending to cache)
    lastHtmlRef.current = fullHTML
    
    try {
      const response = await fetch('/api/preview-cache', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id: previewId, 
          html: fullHTML,
          ttl: 300  // 5 minutes (in seconds)
        }),
        signal: abortControllerRef.current.signal
      })
      
      // Ignore if a newer run started while we were waiting
      if (thisRunId !== runIdRef.current) return
      
      if (!response.ok) {
        console.error('Failed to cache preview:', await response.text())
        return
      }
      
      // Clear srcdoc right before setting new src (srcdoc overrides src per HTML spec)
      iframeRef.current.removeAttribute('srcdoc')
      // Load from preview endpoint (real HTTPS URL enables webcam)
      iframeRef.current.src = `/preview/${previewId}`
      
      // Capture canvas image after sketch has time to render (for Turbo mode)
      setTimeout(() => {
        captureCanvasImage()
      }, 1000)
      
    } catch (error) {
      // AbortError is expected when a newer run cancels this one - ignore it
      if (error.name === 'AbortError') return
      console.error('Preview cache error:', error)
    }
  }
  
  // Capture the canvas image for AI context (Turbo mode)
  const captureCanvasImage = () => {
    try {
      // Try to access canvas inside iframe
      const iframe = iframeRef.current
      if (!iframe || !iframe.contentDocument) return
      
      const canvas = iframe.contentDocument.querySelector('canvas')
      if (!canvas) {
        console.log('[Preview] No canvas found to capture')
        return
      }
      
      // Capture and resize to reduce payload size
      const maxSize = 400
      const scale = Math.min(maxSize / canvas.width, maxSize / canvas.height, 1)
      
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = canvas.width * scale
      tempCanvas.height = canvas.height * scale
      
      const ctx = tempCanvas.getContext('2d')
      ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height)
      
      const dataUrl = tempCanvas.toDataURL('image/png', 0.8)
      setLastCanvasImage(dataUrl)
      console.log('[Preview] Canvas captured for Turbo mode')
    } catch (error) {
      // Canvas may be tainted by cross-origin images - that's okay
      console.log('[Preview] Could not capture canvas:', error.message)
      setLastCanvasImage(null)
    }
  }

  const stopSketch = () => {
    if (!iframeRef.current) return

    // Notify listeners that sketch is stopping (for MobileLayout sync)
    window.dispatchEvent(new CustomEvent('sketchStopped'))

    // Clear any pending debounce timer (prevents restart after stop)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    // Cancel any in-flight fetch and invalidate pending responses
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    runIdRef.current++  // Invalidate any pending run responses

    // Use srcdoc for instant stop (no network request needed)
    iframeRef.current.srcdoc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Stopped</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      background: #f0f0f0;
      font-family: system-ui;
      color: #666;
    }
  </style>
</head>
<body>
  <div style="text-align: center;">
    <div style="font-size: 48px; margin-bottom: 16px;">⏹️</div>
    <div>Sketch stopped</div>
  </div>
</body>
</html>`
  }

  const generateDefaultHTML = () => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
  <title>p5.js Sketch</title>
  <style>
    * {
      /* Prevent double-tap zoom, improve touch responsiveness */
      touch-action: manipulation;
    }
    html, body {
      margin: 0;
      padding: 0;
      overflow: hidden;
      width: 100%;
      height: 100%;
    }
    canvas {
      display: block;
      /* Ensure canvas receives touch events */
      touch-action: none;
    }
  </style>
  <script>
    // Ensure p5.js gets correct window dimensions on startup
    // Sometimes the iframe layout isn't complete when setup() first runs
    window.addEventListener('load', function() {
      // Small delay to ensure layout is finalized
      setTimeout(function() {
        if (typeof windowResized === 'function') {
          windowResized();
        }
      }, 50);
    });
  </script>
</head>
<body>
</body>
</html>`
  }

  // Note: Console message handling is done by EnhancedDebugConsole component
  // which has better error parsing and stack trace display

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const popOutPreview = () => {
    // Open full screen or maximized window
    const popout = window.open('', 'p5.js Preview', 'width=' + screen.availWidth + ',height=' + screen.availHeight + ',left=0,top=0,menubar=no,toolbar=no,location=no,status=no,resizable=yes')
    if (popout && lastHtmlRef.current) {
      // Use stored HTML instead of reading from iframe (avoids cross-origin issues)
      let htmlContent = lastHtmlRef.current
      
      // Add responsive canvas scaling script
      const canvasScalingScript = `
        <script>
          // Responsive canvas scaling for pop-out window
          window.addEventListener('load', function() {
            function resizeCanvases() {
              const canvases = document.querySelectorAll('canvas');
              canvases.forEach(function(canvas) {
                // Get parent or body dimensions
                const container = canvas.parentElement || document.body;
                const w = window.innerWidth;
                const h = window.innerHeight;
                
                // For p5.js canvases
                if (typeof resizeCanvas === 'function' && window.width && window.height) {
                  resizeCanvas(w, h);
                }
                
                // Set pixel density for sharp rendering
                if (typeof pixelDensity === 'function') {
                  pixelDensity(Math.max(1, window.devicePixelRatio || 1));
                }
                
                // CSS sizing
                canvas.style.width = '100%';
                canvas.style.height = '100%';
                canvas.style.maxWidth = '100vw';
                canvas.style.maxHeight = '100vh';
              });
            }
            
            // Initial resize
            setTimeout(resizeCanvases, 100);
            
            // Resize on window resize (with debounce)
            let resizeTimeout;
            window.addEventListener('resize', function() {
              clearTimeout(resizeTimeout);
              resizeTimeout = setTimeout(resizeCanvases, 250);
            });
          });
        </script>
      `;
      
      // Inject before </body>
      htmlContent = htmlContent.replace('</body>', canvasScalingScript + '</body>');
      
      // Ensure proper viewport meta tag
      if (!htmlContent.includes('viewport')) {
        htmlContent = htmlContent.replace('<head>', '<head><meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">');
      }
      
      popout.document.write(htmlContent);
      popout.document.close();
      
      // Try to maximize the window
      popout.moveTo(0, 0);
      popout.resizeTo(screen.availWidth, screen.availHeight);
      
      // Minimize preview in main window
      setIsPoppedOut(true);
      
      // Detect when popup is closed
      const checkClosed = setInterval(() => {
        if (popout.closed) {
          setIsPoppedOut(false);
          clearInterval(checkClosed);
        }
      }, 500);
    }
  }

  // If popped out, show minimized state - IMPORTANT: This renders instead of the full preview
  if (isPoppedOut) {
    return (
      <div className="bg-gray-900 rounded-lg border-2 border-dashed border-gray-700 overflow-hidden flex flex-col h-full items-center justify-center p-8">
        <div className="text-center">
          <ExternalLink size={64} className="mx-auto mb-4 text-p5-pink animate-pulse" />
          <h3 className="text-white text-lg font-semibold mb-2">Preview Popped Out</h3>
          <p className="text-ide-textMuted mb-4 text-sm">Canvas is in a separate window</p>
          <button
            type="button"
            onClick={() => {
              setIsPoppedOut(false)
            }}
            className="px-6 py-3 bg-ide-accent hover:bg-ide-accentHover rounded-lg text-white font-medium shadow-lg transition-all hover:scale-105"
          >
            ↩️ Restore Preview Here
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-ide-panel overflow-hidden flex flex-col h-full ${
      isFullscreen ? 'fixed inset-0 z-50 bg-black' : ''
    }`}>
      {/* Preview header */}
      <div className={`flex items-center justify-between px-3 py-2 border-b border-ide-border ${
        isFullscreen && isMobileOrTablet ? 'bg-ide-surface/90' : 'bg-ide-surface'
      }`}>
        <span className="text-sm font-medium text-ide-text">Preview</span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={runSketch}
            className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
            title="Refresh Preview"
            aria-label="Refresh Preview"
          >
            <RotateCcw size={isMobileOrTablet ? 18 : 14} />
          </button>
          {!isMobileOrTablet && (
            <button
              type="button"
              onClick={popOutPreview}
              className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              title="Pop out Preview"
              aria-label="Pop out Preview"
            >
              <ExternalLink size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={toggleFullscreen}
            className={`p-1.5 rounded transition-colors ${
              isMobileOrTablet ? 'bg-ide-accentMuted text-ide-accent' : 'text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover'
            }`}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={isMobileOrTablet ? 18 : 14} /> : <Maximize2 size={isMobileOrTablet ? 18 : 14} />}
          </button>
        </div>
      </div>

      {/* Preview iframe */}
      <div className="flex-1 bg-black">
        <iframe
          ref={iframeRef}
          className="w-full h-full border-0"
          title="sketch preview"
          sandbox="allow-forms allow-modals allow-pointer-lock allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation allow-downloads"
          allow="accelerometer; autoplay; bluetooth; camera; encrypted-media; geolocation; gyroscope; hid; microphone; magnetometer; midi; payment; usb; serial; xr-spatial-tracking; gamepad; clipboard-read; clipboard-write; fullscreen; display-capture; screen-wake-lock"
        />
        {/* Matches official p5.js web editor PreviewFrame.jsx:
            https://github.com/processing/p5.js-web-editor/blob/develop/client/modules/IDE/components/PreviewFrame.jsx
            
            Sandbox: allow-forms allow-modals allow-pointer-lock allow-popups 
                     allow-same-origin allow-scripts allow-top-navigation-by-user-activation allow-downloads
            
            Allow: accelerometer; ambient-light-sensor; autoplay; bluetooth; camera; 
                   encrypted-media; geolocation; gyroscope; hid; microphone; magnetometer; 
                   midi; payment; usb; serial; vr; xr-spatial-tracking
                   + our extras: gamepad; clipboard-read/write; fullscreen; display-capture; screen-wake-lock */}
      </div>
    </div>
  )
}
